package com.schoolconnect.app.repository.mystudents

import com.schoolconnect.app.datasource.network.KtorApiClient
import com.schoolconnect.app.datasource.network.response.StudentListResponse
import com.schoolconnect.app.datasource.network.response.TeacherStatisticsResponse
import com.schoolconnect.app.datasource.network.response.TeacherStudentListResponse
import com.schoolconnect.app.datasource.network.response.TeacherStudentAttendanceStatsResponse
import com.schoolconnect.app.datasource.network.response.TeacherStudentAttendanceRecordsResponse
import com.schoolconnect.app.datasource.network.response.StudentExamResultRecordsResponse
import com.schoolconnect.app.datasource.network.response.serverTotalCountOrNull
import com.schoolconnect.app.datasource.network.response.withServerTotalCount
import com.schoolconnect.app.feature.common.PaginationPolicy
import com.schoolconnect.app.feature.common.ServerSort
import com.schoolconnect.app.feature.common.ServerSortOrder
import com.schoolconnect.app.util.scLog

object TeacherMyStudentsRepository {
    private const val TAG = "[DATA_RETRIEVE_SC] TeacherMyStudentsRepository"

    suspend fun loadDashboardStatistics(): Result<TeacherStatisticsResponse> {
        scLog("$TAG loadDashboardStatistics start")
        return KtorApiClient.getTeacherDashboardStatistics()
            .onSuccess { response ->
                scLog(
                    "$TAG loadDashboardStatistics success total=${response.totalStudents ?: 0} " +
                        "classes=${response.assignedClassesCount ?: response.assignedClasses.size} " +
                        "good=${response.goodPerformanceCount ?: 0} needsAttention=${response.needsAttentionCount ?: 0}"
                )
            }
            .onFailure { throwable -> scLog("$TAG loadDashboardStatistics failure", throwable) }
    }

    suspend fun loadDashboardStudents(): Result<TeacherStudentListResponse> {
        scLog("$TAG loadDashboardStudents start")
        return loadCompleteTeacherRoster { page ->
            KtorApiClient.getTeacherDashboardStudents(limit = page.safePageSize, offset = page.offset, sortBy = "name", order = "asc")
        }
            .onSuccess { response ->
                scLog(
                    "$TAG loadDashboardStudents success academicYear=${response.academicYearId ?: "<none>"} " +
                    "rows=${response.students.size} total=${response.count ?: response.students.size}"
                )
            }
            .onFailure { throwable -> scLog("$TAG loadDashboardStudents failure", throwable) }
    }

    suspend fun loadStudentDetail(studentId: String): Result<StudentListResponse> {
        val normalizedStudentId = studentId.trim()
        if (normalizedStudentId.isBlank()) return Result.failure(IllegalArgumentException("Student ID is required."))
        scLog("$TAG loadStudentDetail start studentId=$normalizedStudentId")
        return KtorApiClient.getTeacherStudentDetail(normalizedStudentId)
            .onSuccess { response -> scLog("$TAG loadStudentDetail success studentId=${response.id ?: normalizedStudentId}") }
            .onFailure { throwable -> scLog("$TAG loadStudentDetail failure studentId=$normalizedStudentId", throwable) }
    }

    suspend fun loadStudentAttendanceStats(studentId: String): Result<TeacherStudentAttendanceStatsResponse> {
        val normalizedStudentId = studentId.trim()
        if (normalizedStudentId.isBlank()) return Result.failure(IllegalArgumentException("Student ID is required."))
        scLog("$TAG loadStudentAttendanceStats start studentId=$normalizedStudentId")
        return KtorApiClient.getTeacherStudentAttendanceStats(normalizedStudentId)
            .onFailure { throwable -> scLog("$TAG loadStudentAttendanceStats failure studentId=$normalizedStudentId", throwable) }
    }

    suspend fun loadStudentAttendanceRecords(
        studentId: String,
        limit: Int = 50,
        offset: Int = 0,
        fromDate: String? = null,
        toDate: String? = null,
        status: String? = null
    ): Result<TeacherStudentAttendanceRecordsResponse> {
        val normalizedStudentId = studentId.trim()
        if (normalizedStudentId.isBlank()) return Result.failure(IllegalArgumentException("Student ID is required."))
        scLog("$TAG loadStudentAttendanceRecords start studentId=$normalizedStudentId limit=$limit offset=$offset status=${status ?: "<any>"}")
        return KtorApiClient.getTeacherStudentAttendanceRecords(normalizedStudentId, limit, offset, fromDate, toDate, status)
            .onFailure { throwable -> scLog("$TAG loadStudentAttendanceRecords failure studentId=$normalizedStudentId", throwable) }
    }

    suspend fun loadStudentExamResults(studentId: String, limit: Int = 100): Result<StudentExamResultRecordsResponse> {
        val normalizedStudentId = studentId.trim()
        if (normalizedStudentId.isBlank()) return Result.failure(IllegalArgumentException("Student ID is required."))
        scLog("$TAG loadStudentExamResults start studentId=$normalizedStudentId")
        return KtorApiClient.getStudentExamResults(normalizedStudentId, limit = limit)
            .onSuccess { response -> scLog("$TAG loadStudentExamResults success studentId=$normalizedStudentId count=${response.count ?: response.items.orEmpty().size}") }
            .onFailure { throwable -> scLog("$TAG loadStudentExamResults failure studentId=$normalizedStudentId", throwable) }
    }

    suspend fun loadPage(
        query: String = "",
        gender: String? = null,
        classSectionId: String? = null,
        pageIndex: Int = 0,
        pageSize: Int = PaginationPolicy.DEFAULT_PAGE_SIZE,
        sort: ServerSort = ServerSort("name", ServerSortOrder.ASC),
        forceRemote: Boolean = false
    ): Result<TeacherStudentListResponse> {
        val safePageIndex = pageIndex.coerceAtLeast(0)
        val safePageSize = pageSize.coerceIn(1, 100)
        val offset = safePageIndex * safePageSize
        val normalizedQuery = query.trim()
        val normalizedGender = gender?.trim()?.takeIf { it.isNotBlank() }
        val normalizedClassSectionId = classSectionId?.trim()?.takeIf { it.isNotBlank() }
        scLog(
            "$TAG loadPage start classSectionId=${normalizedClassSectionId ?: "<any>"} gender=${normalizedGender ?: "<any>"} q=${normalizedQuery.ifBlank { "<blank>" }} " +
                "pageIndex=$safePageIndex pageSize=$safePageSize " +
                "offset=$offset sort=${sort.cacheKey} forceRemote=$forceRemote"
        )
        return KtorApiClient.getTeacherDashboardStudents(
            query = normalizedQuery,
            gender = normalizedGender,
            classSectionId = normalizedClassSectionId,
            limit = safePageSize,
            offset = offset,
            sortBy = sort.normalizedField,
            order = sort.order.apiValue
        ).onSuccess { response ->
            scLog("$TAG loadPage success classSectionId=${normalizedClassSectionId ?: "<any>"} pageIndex=$safePageIndex pageSize=$safePageSize rows=${response.students.size} total=${response.count}")
        }.onFailure { throwable ->
            scLog("$TAG loadPage failure pageIndex=$safePageIndex pageSize=$safePageSize", throwable)
        }
    }

    /** Loads the complete selected-class roster for workflows that need every student at once. */
    suspend fun loadCompleteClassRoster(classSectionId: String): Result<TeacherStudentListResponse> {
        val normalizedClassSectionId = classSectionId.trim()
        if (normalizedClassSectionId.isBlank()) {
            return Result.failure(IllegalArgumentException("Class is required to load its student roster."))
        }
        scLog("$TAG loadCompleteClassRoster start classSectionId=$normalizedClassSectionId")
        return loadCompleteTeacherRoster { page ->
            KtorApiClient.getTeacherDashboardStudents(
                classSectionId = normalizedClassSectionId,
                limit = page.safePageSize,
                offset = page.offset,
                sortBy = "name",
                order = "asc"
            )
        }.onSuccess { response ->
            scLog("$TAG loadCompleteClassRoster success classSectionId=$normalizedClassSectionId rows=${response.students.size} total=${response.count}")
        }.onFailure { throwable ->
            scLog("$TAG loadCompleteClassRoster failure classSectionId=$normalizedClassSectionId", throwable)
        }
    }

}
